'use client'

import { forwardRef, ReactNode } from 'react'
import { cn } from '../utils/cn'

/**
 * BIRHAUS Container Component
 * 
 * Responsive container implementing BIRHAUS principles:
 * - Consistent spacing and max-width constraints
 * - 8-point grid system alignment
 * - BIRHAUS performance guidelines (optimal content width)
 * - Accessibility compliance with semantic structure
 * - Responsive design with mobile-first approach
 */

export interface BirhausContainerProps {
  // Content
  children: ReactNode
  
  // Container sizing based on BIRHAUS design tokens
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  
  // Padding following 8-point grid system
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  
  // Layout control
  centered?: boolean
  
  // Semantic HTML
  as?: 'div' | 'section' | 'article' | 'main' | 'aside' | 'header' | 'footer'
  
  // Styling
  className?: string
}

export const BirhausContainer = forwardRef<HTMLDivElement, BirhausContainerProps>(({
  children,
  size = 'xl',
  padding = 'md',
  centered = true,
  as: Component = 'div',
  className,
  ...props
}, ref) => {
  // BIRHAUS Design Token: Container max-widths based on content optimization
  const sizeClasses = {
    xs: 'max-w-xs',    // 320px - Mobile content
    sm: 'max-w-sm',    // 384px - Small mobile
    md: 'max-w-md',    // 448px - Large mobile
    lg: 'max-w-4xl',   // 896px - Tablet/small desktop (optimal reading width)
    xl: 'max-w-6xl',   // 1152px - Desktop standard
    '2xl': 'max-w-7xl', // 1280px - Large desktop
    full: 'max-w-full' // Full width
  }

  // BIRHAUS Design Token: 8-point grid system padding
  const paddingClasses = {
    none: '',
    xs: 'px-2 py-1',     // 8px horizontal, 4px vertical
    sm: 'px-4 py-2',     // 16px horizontal, 8px vertical
    md: 'px-6 py-4',     // 24px horizontal, 16px vertical
    lg: 'px-8 py-6',     // 32px horizontal, 24px vertical
    xl: 'px-12 py-8'     // 48px horizontal, 32px vertical
  }

  return (
    <Component
      ref={ref}
      className={cn(
        // Base container styles
        'w-full',
        
        // Size constraints
        // eslint-disable-next-line security/detect-object-injection -- Safe access with known keys from union type
        sizeClasses[size],
        
        // Centering
        centered && 'mx-auto',
        
        // Padding  
        // eslint-disable-next-line security/detect-object-injection -- Safe access with known keys from union type
        paddingClasses[padding],
        
        // Custom styles
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
})

BirhausContainer.displayName = 'BirhausContainer'

// Hook for managing responsive container behavior
export function useBirhausContainer() {
  // Could be extended to handle responsive size changes
  // For now, it's a simple utility for container constants
  return {
    sizes: {
      xs: '320px',
      sm: '384px', 
      md: '448px',
      lg: '896px',
      xl: '1152px',
      '2xl': '1280px',
      full: '100%'
    },
    
    // BIRHAUS guideline: optimal content width for readability
    optimalReadingWidth: 'lg', // 896px - based on typography research
    
    // Common breakpoints for responsive design
    breakpoints: {
      mobile: 'sm',    // Up to 384px
      tablet: 'lg',    // Up to 896px  
      desktop: 'xl',   // Up to 1152px
      wide: '2xl'      // Up to 1280px
    }
  }
}

// Pre-configured container variants for common use cases
export const birhausContainerPresets = {
  // Page layouts
  page: {
    size: 'xl' as const,
    padding: 'lg' as const,
    centered: true
  },
  
  // Content sections
  content: {
    size: 'lg' as const,
    padding: 'md' as const,
    centered: true
  },
  
  // Sidebar content
  sidebar: {
    size: 'sm' as const,
    padding: 'sm' as const,
    centered: false
  },
  
  // Full-width sections (heroes, banners)
  fullWidth: {
    size: 'full' as const,
    padding: 'lg' as const,
    centered: false
  },
  
  // Modal/dialog content
  modal: {
    size: 'md' as const,
    padding: 'md' as const,
    centered: true
  },
  
  // Mobile-optimized
  mobile: {
    size: 'sm' as const,
    padding: 'sm' as const,
    centered: true
  }
}